<script setup>
import { onBeforeUnmount, watch } from "vue";
import { Link, router } from "@inertiajs/vue3";
import { useAuthUser } from "@/composables/useAuthUser.js";
import { useModalogStore } from "@/stores/modalog.js";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import IconDropdownAccountSettings from "~/svg/dropdown-account-settings.svg";
import IconDropdownLogOut from "~/svg/dropdown-log-out.svg";

/**
 * Member mobile navigation sheet (BugHerd BH-36, Figma 487-340).
 *
 * A white sheet that drops over the page from the top: an account band
 * (avatar, greeting, Log Out, Settings), the primary nav with the Friends
 * sub-links, the secondary links, and Create List / Add Gift at the foot.
 * Teleported to <body> so it escapes the header's rounded wrappers, and
 * gates the page scroll while open.
 *
 * The logo and the close control are NOT in here: DashboardHeader's own
 * logo and hamburger stay in place above the sheet (z-[60]) and the
 * hamburger morphs into the ✕, exactly like the guest BrandMobileNav. The
 * spacer at the top mirrors that header row so the rule lands beneath it.
 *
 * Inertia keeps the header mounted across navigations, so every link and
 * action closes the sheet itself (also BugHerd BH-46).
 */
const props = defineProps({
    open: {
        type: Boolean,
        default: false,
    },
    // Primary nav rows from DashboardHeader: { label, href, badge?, children? }.
    items: {
        type: Array,
        required: true,
    },
    // The member's own lists page; the avatar in the account band links here.
    profileHref: {
        type: String,
        required: true,
    },
});

const emit = defineEmits(["close", "closed"]);

const user = useAuthUser();

// Brand marketing pages hop stylesheet regimes, so they load in full.
const secondaryLinks = [
    { label: "Browser Extension", route: "browser-extensions" },
    { label: "Mobile App", route: "mobile" },
    { label: "Help", route: "help" },
];

let savedScrollY = 0;

function close() {
    emit("close");
}

function handleKeydown(event) {
    if (event.key === "Escape" && props.open) {
        close();
    }
}

function logout() {
    close();
    router.post(route("logout"));
}

function openTour() {
    close();
    useModalogStore().showModalog({
        type: "ModalogOnboardingTour",
        props: {},
    });
}

function createList() {
    close();
    useModalogStore().showModalog({
        type: "ModalogCreateListBrand",
        props: { listType: "wish" },
    });
}

// Mirrors Dashboard/ActionButtons: the add-gift modalog shows a
// destination-list picker, so with no lists at all there's nothing to
// pick — create one instead.
function addGift() {
    if (!user.value?.list_count) {
        createList();

        return;
    }

    close();
    useModalogStore().showModalog({
        type: "ModalogAddGiftBrand",
        props: { listType: "wish", listUuid: null },
    });
}

// Lock the document scroll while the sheet is open. The header's hamburger
// doubles as the close control, so the header has to be in view: jump to
// the top on open and put the reader back on close.
watch(
    () => props.open,
    (isOpen, wasOpen) => {
        if (isOpen) {
            savedScrollY = window.scrollY;
            window.scrollTo({ top: 0, behavior: "instant" });
            document.documentElement.style.overflow = "hidden";
            document.addEventListener("keydown", handleKeydown);
        } else if (wasOpen) {
            document.documentElement.style.overflow = "";
            document.removeEventListener("keydown", handleKeydown);
            window.scrollTo({ top: savedScrollY, behavior: "instant" });
        }
    },
    // Immediate so a sheet mounted already-open still locks + listens.
    { immediate: true },
);

onBeforeUnmount(() => {
    document.documentElement.style.overflow = "";
    document.removeEventListener("keydown", handleKeydown);
});
</script>

<template>
    <Teleport to="body">
        <Transition
            enter-active-class="transition-transform duration-300 ease-out"
            enter-from-class="-translate-y-full"
            leave-active-class="transition-transform duration-200 ease-in"
            leave-to-class="-translate-y-full"
            @after-leave="emit('closed')"
        >
            <div
                v-if="open"
                id="dashboard-mobile-nav"
                class="brand fixed inset-0 z-50 overflow-y-auto font-brand text-brand-navy xl:hidden"
                role="dialog"
                aria-modal="true"
                aria-label="Navigation"
            >
                <!-- Click-away area: the page stays visible beneath the sheet
                     (no scrim in the mock), tapping it closes. -->
                <div
                    class="absolute inset-0"
                    aria-hidden="true"
                    data-test="mobile-nav-backdrop"
                    @click="close"
                ></div>

                <div
                    class="relative bg-white pb-[25px] shadow-[0_4px_55px_0_rgba(0,0,0,0.35)]"
                >
                    <!--
                        Spacer matching the header row (52px hamburger inside
                        the page's pt-5 + header's py-4 / sm:py-5) so the logo
                        and ✕ above sit inside it, with the rule beneath.
                    -->
                    <div
                        aria-hidden="true"
                        class="h-[52px] border-b border-solid border-brand-tan pb-6 pt-9 [box-sizing:content-box] sm:pt-10"
                    ></div>

                    <!-- Account band -->
                    <div
                        class="flex items-center gap-5 bg-[#102a3d] px-[39px] py-[26px] text-white"
                    >
                        <Link
                            :href="profileHref"
                            class="shrink-0"
                            aria-label="My Lists"
                            data-test="mobile-nav-avatar"
                            @click="close"
                        >
                            <img
                                :src="
                                    user?.avatar?.square ??
                                    user?.avatar?.profile_photo
                                "
                                :alt="user?.full_name"
                                class="size-[55px] rounded-[2px] object-cover"
                            />
                        </Link>
                        <div class="min-w-0 flex-1">
                            <p
                                class="truncate font-brand text-[18px] font-bold leading-5 text-white"
                            >
                                Welcome, {{ user?.first_name }}
                            </p>
                            <div
                                class="mt-3 flex items-center gap-8 text-[15px] font-semibold"
                            >
                                <button
                                    type="button"
                                    class="inline-flex items-center gap-2 text-white"
                                    @click="logout"
                                >
                                    <IconDropdownLogOut
                                        class="h-4 w-[18px] shrink-0 text-brand-red"
                                        aria-hidden="true"
                                    />
                                    Log Out
                                </button>
                                <Link
                                    :href="route('profile.show')"
                                    class="inline-flex items-center gap-2 text-white"
                                    @click="close"
                                >
                                    <IconDropdownAccountSettings
                                        class="size-[19px] shrink-0 text-brand-red"
                                        aria-hidden="true"
                                    />
                                    Settings
                                </Link>
                            </div>
                        </div>
                    </div>

                    <!--
                        Primary nav: 22px condensed rows on a 45px rhythm. Row
                        heights are explicit (not line-height) because the
                        legacy stylesheet sets its own line-height on li/a and
                        would collapse the rhythm on pages that load app.scss.
                    -->
                    <ul
                        class="px-[37px] pt-[19px] font-brand-display text-[22px] font-bold uppercase leading-none text-brand-navy"
                        data-test="mobile-nav-primary"
                    >
                        <li v-for="item in items" :key="item.label">
                            <!--
                                Explicit color: legacy `a { color: … }` beats
                                inheritance on pages that load app.scss.
                            -->
                            <Link
                                :href="item.href"
                                class="flex h-[45px] items-center text-brand-navy transition hover:text-brand-red"
                                @click="close"
                            >
                                {{ item.label }}
                                <span
                                    v-if="item.badge"
                                    class="ml-2 grid h-[19px] min-w-[19px] place-items-center rounded-full bg-brand-red px-1.5 font-brand text-[11px] font-bold leading-none text-white"
                                    >{{ item.badge }}</span
                                >
                            </Link>
                            <ul
                                v-if="item.children"
                                class="pl-7 font-brand text-[15px] font-semibold normal-case leading-none tracking-normal"
                            >
                                <li
                                    v-for="child in item.children"
                                    :key="child.routeName"
                                >
                                    <Link
                                        :href="route(child.routeName)"
                                        class="flex h-8 items-center text-brand-navy transition hover:text-brand-red"
                                        @click="close"
                                    >
                                        {{ child.label }}
                                    </Link>
                                </li>
                            </ul>
                        </li>
                    </ul>

                    <!-- Secondary links -->
                    <ul
                        class="mt-[13px] px-[37px] text-[15px] leading-none text-[#565a77]"
                        data-test="mobile-nav-secondary"
                    >
                        <li>
                            <button
                                type="button"
                                class="flex h-8 items-center text-left text-[#565a77] transition hover:text-brand-navy"
                                @click="openTour"
                            >
                                Quick Tour
                            </button>
                        </li>
                        <li v-for="link in secondaryLinks" :key="link.route">
                            <a
                                :href="route(link.route)"
                                class="flex h-8 items-center text-[#565a77] transition hover:text-brand-navy"
                            >
                                {{ link.label }}
                            </a>
                        </li>
                    </ul>

                    <!-- Create List / Add Gift, the dashboard's pair -->
                    <div class="mt-6 flex items-center gap-[15px] px-6">
                        <BrandButton
                            icon-rest-class="text-brand-red"
                            class="h-[52px] flex-1 px-4 text-[15px]"
                            data-test="mobile-nav-create-list"
                            @click="createList"
                        >
                            <template #icon>
                                <svg
                                    viewBox="0 0 22 18"
                                    class="h-[18px] w-[22px]"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    aria-hidden="true"
                                >
                                    <path d="M1 3.5h12M1 9h12M1 14.5h7" />
                                    <path d="M18 8.5v8M14 12.5h8" />
                                </svg>
                            </template>
                            Create List
                        </BrandButton>

                        <BrandButton
                            icon-rest-class="text-brand-red"
                            class="h-[52px] flex-1 px-4 text-[15px]"
                            data-test="mobile-nav-add-gift"
                            @click="addGift"
                        >
                            <template #icon>
                                <span
                                    class="grid size-6 place-items-center rounded-full border-2 border-solid border-current"
                                >
                                    <svg
                                        viewBox="0 0 14 14"
                                        class="h-3 w-3"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2.6"
                                        stroke-linecap="round"
                                        aria-hidden="true"
                                    >
                                        <path d="M7 2.5v9M2.5 7h9" />
                                    </svg>
                                </span>
                            </template>
                            Add Gift
                        </BrandButton>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>
